import { Component, Inject, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle
} from "@angular/material/dialog";
import { ITeam } from "@teammood/team";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { CommonModule, NgClass } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatStepper, MatStepperModule, StepperOrientation } from '@angular/material/stepper';
import { debounceTime, distinctUntilChanged, map, Observable, Subject, switchMap, takeUntil, tap } from "rxjs";
import { BreakpointObserver } from "@angular/cdk/layout";
import { DatepickerComponent } from '@teammood/shared/ui/date-picker';
import { MultiTeamSelectorComponent } from '@teammood/shared/ui/multi-team-selector';
import { STEPPER_GLOBAL_OPTIONS } from "@angular/cdk/stepper";
import {
    AnswerChoices,
    CreatePollDialogData,
    PollUnderCreation,
    QuestionForPreview,
    PollTemplate,
    QuestionTypeKey,
    TeamPollTarget,
    RecurrencePreviewOption,
    RecurrenceType
} from "./create-poll.type";
import { ScheduleOptionPreview } from "./poll-preview/poll-preview.type";
import { AnswerChoicesComponent } from "./answer-choices/answer-choices.component";
import { isToday } from 'date-fns';
import { TmSlideToggleComponent } from "@teammood/shared/ui/slide-toggle";
import { PollPreviewComponent } from "./poll-preview/poll-preview.component";
import { CreatePollService } from "./create-poll.service";
import { PollType } from "../polls.type";
import { TipService } from "../../timeline/timeline-item/tips/tip.service";
import { UserService } from "@teammood/shared/util/user";
import { MatSelectModule } from "@angular/material/select";


@Component({
    selector: 'tm-create-poll-dialog',
    templateUrl: 'create-poll-dialog.component.html',
    styleUrls: ['./../../timeline/timeline-item/timeline-item.scss', './create-poll-dialog.component.scss'],
    providers: [
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            useValue: { showError: true },
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        MatDialogTitle,
        TranslateModule,
        MatDialogContent,
        ReactiveFormsModule,
        FormsModule,
        NgClass,
        MatDialogActions,
        MatButtonModule,
        MatStepperModule,
        MatFormFieldModule,
        MatInputModule,
        PollPreviewComponent,
        DatepickerComponent,
        MultiTeamSelectorComponent,
        AnswerChoicesComponent,
        TmSlideToggleComponent,
        MatSelectModule
    ]
})
export class TmCreatePollDialogComponent implements OnInit, OnDestroy {

    protected recurrenceOptions: RecurrenceType[] = ['once', 'weekly', 'monthly'];
    protected poll: PollUnderCreation;
    protected pollForPreview: QuestionForPreview;
    protected previewType: PollType;
    protected possibleAnswerOptionTypes: QuestionTypeKey[] = [
        { type: 'AgreeDisagreeQuestion', translateKey: 'poll_creation.type.agree_disagree_question'}, 
        { type: 'ScaleQuestion', translateKey: 'poll_creation.type.scale_question'},
        { type: 'ClosedQuestion', translateKey: 'poll_creation.type.closed_question'},
        { type: 'FiveStarsQuestion', translateKey: 'poll_creation.type.five_stars_question'}
    ]
    protected pollsTemplates: PollTemplate[];
    protected teams: ITeam[];
    protected options: ScheduleOptionPreview;
    protected recurrence: RecurrencePreviewOption;
    protected recipients: number;
    protected selectedTeams: ITeam[];    
    protected authorName: string;
    protected areOptionsValid: boolean = true;
    protected recurrenceErrorMessage: string;

    protected showIntro: boolean = true;
    protected dontShowAgainCtrl = new FormControl<boolean>(false);

    private _destroy$ = new Subject<void>();

    stepperOrientation: Observable<StepperOrientation>;

    chooseFormGroup = new FormGroup({
        labelCtrl: new FormControl<string>('', [Validators.required, Validators.minLength(10)]),
    })
    customizeFormGroup = new FormGroup({
        labelCtrl: new FormControl<string>('', [Validators.required, Validators.minLength(10)]),
        toggleDisplayAuthorCtrl: new FormControl<boolean>(true, [Validators.required]),
        toggleAllowCommentsCtrl: new FormControl<boolean>(true, [Validators.required]),
        typeCtrl: new FormControl<PollType>(null, [Validators.required]),
    })
    targetFormGroup = new FormGroup({
        teamIdsCtrl: new FormControl<string[]>([], [Validators.required]),
    })
    scheduleFormGroup = new FormGroup({
        publicationDateCtrl: new FormControl<Date>(new Date(), [Validators.required]),
        displayDurationCtrl: new FormControl<number>(10, [Validators.required]),
        recurrenceCtrl: new FormControl<RecurrenceType>('once', [Validators.required]),
    })

    get labelCtrlChoose() {
        return this.chooseFormGroup.get('labelCtrl');
    }
    get labelCtrlCustomize() {
        return this.customizeFormGroup.get('labelCtrl');
    }
    get typeCtrl() {
        return this.customizeFormGroup.get('typeCtrl');
    }

    get teamIdsCtrl() {
        return this.targetFormGroup.get('teamIdsCtrl');
    }
    get publicationDateCtrl() {
        return this.scheduleFormGroup.get('publicationDateCtrl');
    }
    get displayDurationCtrl() {
        return this.scheduleFormGroup.get('displayDurationCtrl');
    }
    get recurrenceCtrl() {
        return this.scheduleFormGroup.get('recurrenceCtrl');
    }

    constructor(
        @Inject(MAT_DIALOG_DATA) protected dialogInput: CreatePollDialogData,
        private dialogRef: MatDialogRef<TmCreatePollDialogComponent>,
        private translate: TranslateService,
        private createPollService: CreatePollService,
        private tipService: TipService,
        private userService: UserService,
    ) {
        const breakpointObserver = inject(BreakpointObserver);
        this.stepperOrientation = breakpointObserver
          .observe('(min-width: 800px)')
          .pipe(map(({matches}) => (matches ? 'horizontal' : 'vertical')));
    }

    ngOnInit() {
        this.teams = this.dialogInput.teams;
        this.pollsTemplates = this.dialogInput.pollTemplates;
        this.authorName = this.dialogInput.author;
        this.recurrenceErrorMessage = this.translate.instant('poll_creation.schedule_duration_error');

        this.poll = PollUnderCreation.fromEmpty();

        if(this.pollsTemplates.length === 0){ 
            this.setParametersForFromScratchPollCreation();
        }
        if(this.pollsTemplates.length === 1){ 
            this.setParametersForFromTemplatePollCreation(this.dialogInput.pollTemplates[0]);
        }

        this.typeCtrl.valueChanges.pipe(
            distinctUntilChanged(),
            tap(answerType => {
                this.onUpdateAnswerOptionsType(answerType)
            }),
            takeUntil(this._destroy$)
        ).subscribe();

        this.labelCtrlCustomize.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(label => {
                this.poll.label = label;
                this.pollForPreview.label = label;
            }),
            takeUntil(this._destroy$)

        ).subscribe();

        this.displayDurationCtrl.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(async (duration) => {
                this.poll.scheduleOptions.displayDuration = duration;
                if(this.poll.scheduleOptions.publicationDate && this.poll.scheduleOptions.displayDuration){
                    this.options = await this.createPollService.getScheduleOptions(this.poll.scheduleOptions);
                    this.recurrence = this.createPollService.getRecurrenceOptions(this.poll.scheduleOptions.publicationDate, this.poll.scheduleOptions.recurrence);
                }
            }),
            takeUntil(this._destroy$)
        ).subscribe();

        this.userService.get().then(async (user) => {
            if (user) {
                if (user.dismissedTips.includes('poll-intro-tips')) {
                    this.showIntro = false;
                }
            }
        })

        this.dontShowAgainCtrl.valueChanges.pipe(
            distinctUntilChanged(),
            switchMap(dontShowAgain => {
                if (dontShowAgain) {
                    return this.tipService.dismissTipsById('poll-intro-tips');
                } else {
                    return this.tipService.restoreTips('poll-intro-tips');
                }
            }),
            takeUntil(this.dialogRef.afterClosed())
        ).subscribe();

        this.recurrenceCtrl.valueChanges.pipe(
            distinctUntilChanged(),
            tap((recurrence) => {
                this.poll.scheduleOptions.recurrence = recurrence;
                this.recurrence = this.createPollService.getRecurrenceOptions(this.poll.scheduleOptions.publicationDate, recurrence);
                const currentDuration = this.displayDurationCtrl.getRawValue();
                switch(recurrence){
                    case 'once':
                        this.displayDurationCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(30)]);
                        this.recurrenceErrorMessage = this.translate.instant('poll_creation.schedule_duration_error');
                        this.displayDurationCtrl.setValue(currentDuration); //needed to clear error message
                        break;
                    case 'weekly':
                        this.displayDurationCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(7)]);
                        this.recurrenceErrorMessage = this.translate.instant('polls_settings.make_recurrent.duration_error.weekly');
                        if(currentDuration > 7){
                            this.displayDurationCtrl.setValue(7);
                        }
                        break;
                    case 'monthly':
                        this.displayDurationCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(15)]);
                        this.recurrenceErrorMessage = this.translate.instant('polls_settings.make_recurrent.duration_error.monthly')
                        if(currentDuration > 15){
                            this.displayDurationCtrl.setValue(15);
                        }
                        break;
                }
            })
        ).subscribe();
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    protected setParametersForFromScratchPollCreation(){
        this.poll = PollUnderCreation.fromEmpty();
        this.pollForPreview = {
            label: '',
            choices: []
        }
        this.previewType = 'ClosedQuestion';
        this.typeCtrl.setValue(this.poll.answerOptions.type);
        this.recurrenceCtrl.setValue('once');
        this.areOptionsValid = false;
        this.setFocus('questionLabel');
    }

    setParametersForFromTemplatePollCreation(template: PollTemplate){
        this.poll = PollUnderCreation.fromTemplate(template);
        this.labelCtrlChoose.setValue(this.poll.label);
        this.labelCtrlCustomize.setValue(this.poll.label);
        this.typeCtrl.setValue(this.poll.answerOptions.type);
        this.recurrenceCtrl.setValue('once');
        this.previewType = this.poll.answerOptions.type;
        this.pollForPreview = {
            label: this.poll.label,
            choices: PollUnderCreation.fromChoiceListToTypedAnswerOption(this.poll.answerOptions, this.translate)
        }
    }

    protected select(template: PollTemplate, stepper?: MatStepper){
        const templatePoll = template ? template : {
            label: this.translate.instant('poll_creation.type'),
            answerOptions: { type: 'ClosedQuestion', choices: [{label: 'option 1', id: '1', position: 1}, {label: 'option 2', id: '2', position: 2}] }
        } as PollTemplate
        // fill preview
        this.poll = PollUnderCreation.fromTemplate(templatePoll);
        this.labelCtrlChoose.setValue(this.poll.label);
        this.labelCtrlCustomize.setValue(this.poll.label);
        this.typeCtrl.setValue(this.poll.answerOptions.type);
        this.recurrenceCtrl.setValue('once');
        this.previewType = this.poll.answerOptions.type;
        this.pollForPreview = {
            label: this.poll.label,
            choices: templatePoll.answerOptions.choices
        }
        if(!template){
            stepper.next();
        }
    }

    protected onUpdateAnswerOptionsType(answerType: PollType){
        // update options model
        this.poll.answerOptions = {
            type: answerType,
            choices: PollUnderCreation.choiceListFromNewType(this.poll.answerOptions, this.translate)
        }
        // update preview
        this.previewType = answerType;
        this.pollForPreview = {
            label: this.poll.label,
            choices: PollUnderCreation.fromChoiceListToTypedAnswerOption(this.poll.answerOptions, this.translate)
        }
        // valid options are provided and filled with other types
        if(answerType !== 'ClosedQuestion'){
            this.areOptionsValid = true;
        }
    }

    protected onAnswerOptionChange(newValue: { options: AnswerChoices, optionsValidity: boolean}){
        this.poll.answerOptions = newValue.options;
        this.pollForPreview = {
            label: this.poll.label,
            choices: PollUnderCreation.fromChoiceListToTypedAnswerOption(this.poll.answerOptions, this.translate)
        }
        this.areOptionsValid = newValue.optionsValidity;
    }

    protected async uptadeTeams(selectedTeams: ITeam[]){
        if(this.poll){
            const teamIds: string[] = selectedTeams.map(team => team.id);
            const teamTarget = new TeamPollTarget();
            teamTarget.teamIds = teamIds;
            this.poll.target = teamTarget;
            this.teamIdsCtrl.setValue(teamIds);
            this.selectedTeams = selectedTeams;
    
            if(teamTarget.teamIds.length > 0){
                //call to get recipients from team ids
                this.recipients = await this.createPollService.getPollRecipients(teamTarget.teamIds);
            }else{
                this.recipients = 0;
            }
        }
    }

    protected async updatePublicationDate(date: Date){
        this.poll.scheduleOptions.publicationDate = date;
        this.publicationDateCtrl.setValue(date);
        if(this.poll.scheduleOptions.publicationDate && this.poll.scheduleOptions.displayDuration){
            this.options = await this.createPollService.getScheduleOptions(this.poll.scheduleOptions);
            this.recurrence = this.createPollService.getRecurrenceOptions(this.poll.scheduleOptions.publicationDate, this.poll.scheduleOptions.recurrence);
        }
    }

    protected toggleDisplayAuthor(){
        this.poll.toggleDisplayAuthor();
        this.authorName = this.poll._displayAuthor ? this.dialogInput.author : undefined;
    }

    protected toggleAllowComments(){
        this.poll.toggleAllowComments();
    }

    protected onClose() {
        this.dialogRef.close();
    }

    protected createPoll(serial) {
        if(serial){
            this.poll.toggleSerializePoll();
        }
        this.dialogRef.close(this.poll);
    }

    protected isToday(date: Date){
        return isToday(date);
    }

    private setFocus(targetInput: string) {
        setTimeout(function waitTargetElem() {
            let targetElem = document.getElementById(targetInput);
          if (document.body.contains(targetElem)) {
            targetElem.focus();
          } else {
            setTimeout(waitTargetElem, 100);
          }
        }, 300);
      }
    
      onChange(event: any) {
        let index = event.selectedIndex;
        if(index === 1){
            this.setFocus('questionLabel');
        }
      }
}
